import { useState, useEffect } from 'react';
import { RefreshCw, ExternalLink, Maximize2, Minimize2, AlertCircle, GitBranch, Layers } from 'lucide-react';
import api from '../services/api';
import FlowkitDashboardTab from './FlowkitDashboardTab';

type SubPanel = 'flowboard' | 'flowkit';

interface HealthState {
  status: string;
  [key: string]: any;
}

export default function FlowPanel() {
  const [activePanel, setActivePanel] = useState<SubPanel>('flowboard');
  const [flowboardHealth, setFlowboardHealth] = useState<HealthState | null>(null);
  const [flowkitHealth, setFlowkitHealth] = useState<HealthState | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    checkFlowboardHealth();
    checkFlowkitHealth();
  }, []);

  const checkFlowboardHealth = async () => {
    try {
      const r = await api.get('/video/health');
      setFlowboardHealth(r.data);
    } catch {
      setFlowboardHealth({ status: 'disconnected' });
    }
  };

  const checkFlowkitHealth = async () => {
    try {
      const r = await api.get('/flowkit/health');
      setFlowkitHealth(r.data);
    } catch {
      setFlowkitHealth({ status: 'disconnected' });
    }
  };

  const fbOk = flowboardHealth?.status === 'ok';
  const fkOk = flowkitHealth?.status === 'ok';

  const panels = [
    {
      id: 'flowboard' as SubPanel,
      label: 'Flowboard',
      icon: GitBranch,
      health: flowboardHealth,
      isOk: fbOk,
      src: '/flowboard/',
      externalHref: '/flowboard/',
      onRefresh: checkFlowboardHealth,
      description: 'Infinite-canvas AI media workflow composer (Veo 3 · Gemini)',
    },
    {
      id: 'flowkit' as SubPanel,
      label: 'Flowkit',
      icon: Layers,
      health: flowkitHealth,
      isOk: fkOk,
      src: '/flowkit-dashboard/',
      externalHref: '/flowkit-dashboard/',
      onRefresh: checkFlowkitHealth,
      description: 'Google Flow video generation pipeline & job queue',
    },
  ];

  const current = panels.find(p => p.id === activePanel)!;

  return (
    <div className={`h-full flex flex-col ${isFullscreen ? 'studio-shell fixed inset-0 z-50' : ''}`}>
      {/* ── Toolbar ── */}
      <div className="studio-toolbar flex items-center gap-2 border-b px-4 py-2">
        {/* Sub-panel switcher */}
        <div className="flex items-center gap-1 rounded-lg border border-[#d6bd7c]/80 bg-[#fff8ec]/70 p-1">
          {panels.map(p => (
            <button
              key={p.id}
              onClick={() => setActivePanel(p.id)}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-bold transition-colors ${
                activePanel === p.id
                  ? 'bg-[#865916] text-[#fff8ec] shadow-sm'
                  : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/60 hover:text-[#2f2414]'
              }`}
            >
              <p.icon className="w-4 h-4" />
              {p.label}
            </button>
          ))}
        </div>

        {/* Health indicators */}
        <div className="flex items-center gap-3 text-xs ml-2">
          {panels.map(p => (
            <span
              key={p.id}
              className={`flex items-center gap-1 font-bold ${
                p.isOk ? 'text-[#476b24]' : 'text-[#865916]'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${p.isOk ? 'bg-[#76a441]' : 'bg-[#c8942e]'}`} />
              {p.label}
            </span>
          ))}
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <button
          onClick={current.onRefresh}
          className="studio-button-quiet rounded-md p-1.5"
          title="Refresh status"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="studio-button-quiet rounded-md p-1.5"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
        <a
          href={current.externalHref}
          target="_blank"
          rel="noopener noreferrer"
          className="studio-button-quiet rounded-md p-1.5"
          title="Open in new tab"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* ── Offline Banner ── */}
      {!current.isOk && current.health !== null && (
        <div className="mx-4 mt-2 flex items-center gap-2 rounded border border-[#c8942e]/50 bg-[#c8942e]/10 px-3 py-1.5 text-xs font-medium text-[#6d4200]">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>
            <strong>{current.label}</strong> agent is not reachable.
            {' '}{current.id === 'flowboard'
              ? 'Start the flowboard-agent container and reload.'
              : 'Start the flowkit-agent container and reload.'}
          </span>
        </div>
      )}

      {/* ── Split canvas area ── */}
      <div className="flex-1 relative overflow-hidden">
        {panels.map(p => (
          <div
            key={p.id}
            className={`absolute inset-0 transition-opacity duration-150 ${
              activePanel === p.id ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
          >
            {p.id === 'flowkit' && (p.isOk || p.health === null) ? (
              <FlowkitDashboardTab />
            ) : p.isOk || p.health === null ? (
              <iframe
                key={`${p.id}-iframe`}
                src={p.src}
                className="w-full h-full border-0"
                title={`${p.label} Panel`}
                allow="clipboard-write; clipboard-read"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-[#fdf6e9]">
                <div className="text-center max-w-sm">
                  <p.icon className="w-12 h-12 text-[#d6bd7c] mx-auto mb-4" />
                  <p className="text-sm font-bold text-[#6d5b3f] mb-1">{p.label} is offline</p>
                  <p className="text-xs text-[#a89470]">{p.description}</p>
                  <button
                    onClick={p.onRefresh}
                    className="mt-4 flex items-center gap-1.5 mx-auto rounded-md bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6d4712] transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
